import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ContratosDoCliente } from '../contratos/ContratosDoCliente';
import { ArrowLeft, MapPin, Pencil, Phone, Plus, Mail, Navigation, BellRing, Boxes, QrCode } from 'lucide-react';
import { Etiqueta } from '../ativos/Etiqueta';
import { FormAtivo } from '../ativos/FormAtivo';
import { api, ErroApi } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { NOME_PAPEL_CONTATO, NOME_STATUS, NOME_TIPO, endereco, telefone } from '../../componentes/formatos';
import { FormCliente, type DadosCliente } from './FormCliente';
import { FormLocal, type DadosLocal } from './FormLocal';
import { FormContato, type DadosContato } from './FormContato';
import { AjustesCliente } from '../checklists/AjustesCliente';

interface Detalhe extends DadosCliente { id: string; versao: number; criado_em: string; locais: (DadosLocal & { id: string; equipamentos: number; qr_code: string | null })[]; contatos: (DadosContato & { id: string })[] }
type Edicao = { tipo: 'ativo'; local: { id: string; nome: string }; via?: boolean } | { tipo: 'cliente' } | { tipo: 'local'; item?: DadosLocal } | { tipo: 'contato'; item?: DadosContato } | null;

export function DetalheCliente() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [c, setC] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<Edicao>(null);
  const [etiqueta, setEtiqueta] = useState<{ codigo: string; parque: string } | null>(null);
  const [erroEtiqueta, setErroEtiqueta] = useState<string | null>(null);
  const podeEditar = pode(usuario, 'cadastro.editar');

  const carregar = useCallback(() => {
    api<Detalhe>(`/clientes/${id}`).then(setC).catch((e) => setErro(e instanceof ErroApi && e.status === 404 ? 'Cliente não encontrado.' : e.message));
  }, [id]);
  useEffect(carregar, [carregar]);
  const salvo = () => { setEdicao(null); carregar(); };
  // etiqueta do parque (0029): gera na primeira vez; depois é sempre a mesma
  async function abrirEtiqueta(l: { id: string; nome: string; qr_code: string | null }) {
    setErroEtiqueta(null);
    try {
      const codigo = l.qr_code ?? (await api<{ qr_code: string }>(`/locais/${l.id}/qr`, { metodo: 'POST' })).qr_code;
      setEtiqueta({ codigo, parque: l.nome });
      if (!l.qr_code) carregar();
    } catch (e) { setErroEtiqueta(e instanceof Error ? e.message : 'Não foi possível gerar a etiqueta.'); }
  }

  if (erro) return <><Link to="/clientes" className="voltar"><ArrowLeft size={16} /> Clientes</Link><p className="alerta">{erro}</p></>;
  if (!c) return <p className="nota">Carregando…</p>;

  const nomeLocal = (lid: string | null) => c.locais.find((l) => l.id === lid)?.nome;

  return (
    <>
      <Link to="/clientes" className="voltar"><ArrowLeft size={16} aria-hidden /> Clientes</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{NOME_TIPO[c.tipo]}</span>
          <h1 className="titulo">{c.razao_social}</h1>
          <div className="meta">
            <span className={`status status-${c.status}`}>{NOME_STATUS[c.status]}</span>
            {c.nome_fantasia && <span>{c.nome_fantasia}</span>}
            <span className="mono">{c.documento ?? 'sem CPF/CNPJ'}</span>
            {c.origem === 'conta_azul' && <span className="origem-erp" title="Razão social, CPF/CNPJ e situação vêm do Conta Azul">Conta Azul</span>}
          </div>
        </div>
        {podeEditar && <button className="btn-fantasma" onClick={() => setEdicao({ tipo: 'cliente' })}><Pencil size={16} aria-hidden /> Editar</button>}
      </div>
      {c.observacoes && <p className="observacoes">{c.observacoes}</p>}

      <section className="secao">
        <div className="secao-topo">
          <h2>Parques <span className="contador">{c.locais.length}</span></h2>
          {erroEtiqueta && <p className="alerta" role="alert">{erroEtiqueta}</p>}
          {podeEditar && <button className="btn-fantasma" onClick={() => setEdicao({ tipo: 'local' })}><Plus size={16} aria-hidden /> Parque</button>}
        </div>
        {c.locais.length === 0 ? (
          <p className="nota">Nenhum parque cadastrado. Parque é o terreno onde o técnico faz check-in — um condomínio costuma ter um só; portaria, perímetro e ruas viram setores dentro dele.</p>
        ) : (
          <div className="grade-locais">
            {c.locais.map((l) => (
              <article key={l.id} className={`cartao local${l.ativo ? '' : ' inativo'}`}>
                <div className="local-topo">
                  <div className="icone-tile pequeno"><MapPin size={20} strokeWidth={1.7} aria-hidden /></div>
                  <div>
                    <h3>{l.nome}{!l.ativo && <span className="pill-breve"><i aria-hidden /> fora de uso</span>}</h3>
                    <p>{endereco(l)}</p>
                  </div>
                  <span className="botoes-icone">
                    {(podeEditar || l.qr_code) && <button className="btn-icone" onClick={() => abrirEtiqueta(l)} aria-label={`Etiqueta do parque ${l.nome}`} title="Etiqueta do parque"><QrCode size={16} /></button>}
                    {podeEditar && <button className="btn-icone" onClick={() => setEdicao({ tipo: 'local', item: l })} aria-label={`Editar ${l.nome}`}><Pencil size={16} /></button>}
                  </span>
                </div>
                <div className="local-rodape">
                  {l.latitude != null ? (
                    <a href={`https://www.google.com/maps?q=${l.latitude},${l.longitude}`} target="_blank" rel="noreferrer"><Navigation size={14} aria-hidden /> Ver no mapa · check-in até {l.raio_geofence_m} m</a>
                  ) : <span className="sem-geo">Sem coordenadas: o check-in não vai conferir presença</span>}
                </div>
                {l.instrucoes_acesso && <p className="instrucoes">{l.instrucoes_acesso}</p>}
                <div className="local-equipamentos">
                  <Link to={`/ativos?local_id=${l.id}`}><Boxes size={14} aria-hidden /> {l.equipamentos} equipamento{l.equipamentos === 1 ? '' : 's'}</Link>
                  {podeEditar && l.ativo && <span className="acoes-parque">
                    <button className="btn-link" onClick={() => setEdicao({ tipo: 'ativo', local: { id: l.id, nome: l.nome }, via: true })}>+ via</button>
                    <button className="btn-link" onClick={() => setEdicao({ tipo: 'ativo', local: { id: l.id, nome: l.nome } })}>+ equipamento</button>
                  </span>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <ContratosDoCliente clienteId={c.id} />

      <section className="secao">
        <div className="secao-topo">
          <h2>Contatos <span className="contador">{c.contatos.length}</span></h2>
          {podeEditar && <button className="btn-fantasma" onClick={() => setEdicao({ tipo: 'contato' })}><Plus size={16} aria-hidden /> Contato</button>}
        </div>
        {c.contatos.length === 0 ? <p className="nota">Nenhum contato cadastrado.</p> : (
          <div className="cartao tabela-cartao">
            <table className="tabela tabela-contatos">
              <thead><tr><th>Nome</th><th>Papel</th><th>Telefone</th><th>E-mail</th><th>Parque</th>{podeEditar && <th aria-label="Ações" />}</tr></thead>
              <tbody>
                {c.contatos.map((k) => (
                  <tr key={k.id} className={k.ativo ? '' : 'inativo'}>
                    <td><strong>{k.nome}</strong>{k.recebe_os && <small className="com-icone"><BellRing size={12} aria-hidden /> recebe aviso de OS</small>}</td>
                    <td>{NOME_PAPEL_CONTATO[k.papel]}</td>
                    <td>{k.telefone ? <a href={`https://wa.me/${k.telefone}`} target="_blank" rel="noreferrer" className="com-icone"><Phone size={13} aria-hidden /> {telefone(k.telefone)}</a> : '—'}</td>
                    <td>{k.email ? <a href={`mailto:${k.email}`} className="com-icone"><Mail size={13} aria-hidden /> {k.email}</a> : '—'}</td>
                    <td>{nomeLocal(k.local_id) ?? 'Todos'}</td>
                    {podeEditar && <td className="acoes"><button className="btn-icone" onClick={() => setEdicao({ tipo: 'contato', item: k })} aria-label={`Editar ${k.nome}`}><Pencil size={16} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AjustesCliente clienteId={c.id} clienteNome={c.nome_fantasia ?? c.razao_social} locais={c.locais} />

      <Modal titulo={edicao?.tipo === 'ativo' && edicao.via ? 'Nova via de acesso' : 'Novo equipamento'} aberto={edicao?.tipo === 'ativo'} aoFechar={() => setEdicao(null)}>
        {edicao?.tipo === 'ativo' && <FormAtivo localFixo={{ id: edicao.local.id, nome: edicao.local.nome, clienteId: c.id, cliente: c.nome_fantasia ?? c.razao_social }}
          categoriaInicial={edicao.via ? 'agrupador.via' : undefined} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />}
      </Modal>
      <Modal titulo="Editar cliente" aberto={edicao?.tipo === 'cliente'} aoFechar={() => setEdicao(null)}>
        <FormCliente inicial={c} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />
      </Modal>
      <Modal titulo={edicao?.tipo === 'local' && edicao.item ? 'Editar parque' : 'Novo parque'} aberto={edicao?.tipo === 'local'} aoFechar={() => setEdicao(null)}>
        <FormLocal clienteId={c.id} inicial={edicao?.tipo === 'local' ? edicao.item : undefined} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />
      </Modal>
      <Modal titulo="Etiqueta do parque" aberto={etiqueta != null} aoFechar={() => setEtiqueta(null)}>
        {etiqueta && <Etiqueta codigo={etiqueta.codigo} tipo="parque" titulo={etiqueta.parque} sub={c.nome_fantasia || c.razao_social} />}
      </Modal>
      <Modal titulo={edicao?.tipo === 'contato' && edicao.item ? 'Editar contato' : 'Novo contato'} aberto={edicao?.tipo === 'contato'} aoFechar={() => setEdicao(null)}>
        <FormContato clienteId={c.id} locais={c.locais.filter((l) => l.ativo)} inicial={edicao?.tipo === 'contato' ? edicao.item : undefined} aoSalvar={salvo} aoCancelar={() => setEdicao(null)} />
      </Modal>
    </>
  );
}
